import { useState } from 'react';
import type { Trade } from '@/mocks/technical';
import {
  byDirection,
  bySession,
  byWeekday,
  buildDrawdownCurve,
  computeKpis,
  splitPeriod,
  type Kpis,
} from '../../calculations';
import { fmtUsd } from '../ui';

function HealthRow({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1.5 font-mono text-xl font-bold tabular-nums text-slate-900">{value}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{note}</p>
    </div>
  );
}

export function HealthGrid({ kpis }: { kpis: Kpis }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
      <HealthRow label="Win Rate" value={`${kpis.winRate.toFixed(1)}%`} note="Tỷ lệ số lệnh thắng trên tổng số lệnh." />
      <HealthRow label="Profit Factor" value={kpis.profitFactor === Infinity ? '∞' : kpis.profitFactor.toFixed(2)} note="Tổng lợi nhuận chia cho tổng thua lỗ." />
      <HealthRow label="Avg Win / Avg Loss" value={kpis.avgWinLossRatio.toFixed(2)} note="Quy mô lệnh thắng so với lệnh thua trung bình." />
      <HealthRow label="Max Drawdown" value={`${kpis.maxDrawdown.toFixed(1)}%`} note="Mức sụt giảm lớn nhất từ đỉnh vốn." />
      <HealthRow label="Consecutive Wins" value={`${kpis.consecutiveWins}`} note="Chuỗi lệnh thắng liên tiếp dài nhất." />
      <HealthRow label="Consecutive Losses" value={`${kpis.consecutiveLosses}`} note="Chuỗi lệnh thua liên tiếp dài nhất." />
    </div>
  );
}

function StatMini({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'pos' | 'neg' }) {
  const cls = tone === 'pos' ? 'text-emerald-600' : tone === 'neg' ? 'text-rose-600' : 'text-slate-800';
  return (
    <div>
      <p className="text-[10px] font-medium uppercase text-slate-400">{label}</p>
      <p className={`mt-0.5 font-mono text-sm font-bold tabular-nums ${cls}`}>{value}</p>
    </div>
  );
}

function DirectionCard({ label, icon, accent, stat }: { label: string; icon: string; accent: string; stat: { count: number; winRate: number; netPnl: number; avgTrade: number } }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}1a`, color: accent }}>
          <i className={icon} />
        </span>
        <h4 className="text-sm font-bold text-slate-800">{label}</h4>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <StatMini label="Trade Count" value={`${stat.count}`} />
        <StatMini label="Win Rate" value={`${stat.winRate.toFixed(1)}%`} />
        <StatMini label="Net P/L" value={fmtUsd(stat.netPnl)} tone={stat.netPnl >= 0 ? 'pos' : 'neg'} />
        <StatMini label="Avg. Trade" value={fmtUsd(stat.avgTrade)} tone={stat.avgTrade >= 0 ? 'pos' : 'neg'} />
      </div>
    </div>
  );
}

export function DirectionComparison({ trades }: { trades: Trade[] }) {
  const dirs = byDirection(trades);
  const long = dirs.find((d) => d.label.startsWith('Long'))!;
  const short = dirs.find((d) => d.label.startsWith('Short'))!;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <DirectionCard label={long.label} icon="ri-arrow-up-circle-line" accent="#10b981" stat={long} />
      <DirectionCard label={short.label} icon="ri-arrow-down-circle-line" accent="#f43f5e" stat={short} />
    </div>
  );
}

export function TimeAnalysis({ trades }: { trades: Trade[] }) {
  const weekdays = byWeekday(trades);
  const hasSession = trades.some((t) => t.session);
  const sessions = bySession(trades);
  const [session, setSession] = useState<'Asia' | 'London' | 'New York'>('London');
  const activeSession = sessions.find((s) => s.label === session)!;

  return (
    <div>
      {/* Weekday table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3 font-semibold">Ngày</th>
              <th className="py-2 pr-3 font-semibold">Số lệnh</th>
              <th className="py-2 pr-3 font-semibold">Tỷ lệ thắng</th>
              <th className="py-2 pr-3 font-semibold">Lãi/Lỗ ròng</th>
              <th className="py-2 font-semibold">Trung bình</th>
            </tr>
          </thead>
          <tbody>
            {weekdays.map((d) => (
              <tr key={d.label} className="border-b border-slate-100">
                <td className="py-2.5 pr-3 text-sm font-semibold text-slate-700">{d.label}</td>
                <td className="py-2.5 pr-3 font-mono text-xs text-slate-500">{d.count}</td>
                <td className="py-2.5 pr-3 font-mono text-xs text-slate-500">{d.count ? `${d.winRate.toFixed(0)}%` : '—'}</td>
                <td className={`py-2.5 pr-3 font-mono text-xs font-semibold ${d.netPnl >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {d.count ? fmtUsd(d.netPnl) : '—'}
                </td>
                <td className={`py-2.5 font-mono text-xs ${d.avgTrade >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {d.count ? fmtUsd(d.avgTrade) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Session analysis */}
      {hasSession && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h4 className="text-sm font-bold text-slate-800">Phiên giao dịch (Session)</h4>
            <div className="flex gap-1 rounded-full bg-slate-100 p-1">
              {(['Asia', 'London', 'New York'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSession(s)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    session === s ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
              <StatMini label="Trade Count" value={`${activeSession.count}`} />
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
              <StatMini label="Win Rate" value={activeSession.count ? `${activeSession.winRate.toFixed(1)}%` : '—'} />
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
              <StatMini label="Net P/L" value={activeSession.count ? fmtUsd(activeSession.netPnl) : '—'} tone={activeSession.netPnl >= 0 ? 'pos' : 'neg'} />
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
              <StatMini label="Avg. Trade" value={activeSession.count ? fmtUsd(activeSession.avgTrade) : '—'} tone={activeSession.avgTrade >= 0 ? 'pos' : 'neg'} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DrawdownAnalysis({ trades }: { trades: Trade[] }) {
  const kpis = computeKpis(trades);
  const dd = buildDrawdownCurve(trades);
  const maxDd = Math.max(...dd.map((d) => d.drawdown), 0);
  const positive = dd.filter((d) => d.drawdown > 0);
  const avgDd = positive.length ? positive.reduce((s, d) => s + d.drawdown, 0) / positive.length : 0;

  // Recovery time: longest run from peak to a new peak (approximated by consecutive non-zero drawdown)
  let recovery = 0;
  let run = 0;
  for (const d of dd) {
    if (d.drawdown > 0) {
      run += 1;
    } else {
      if (run > recovery) recovery = run;
      run = 0;
    }
  }
  if (run > recovery) recovery = run;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
        <StatMini label="Maximum Drawdown" value={`-${maxDd.toFixed(0)} USD`} tone="neg" />
      </div>
      <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
        <StatMini label="Average Drawdown" value={`-${avgDd.toFixed(0)} USD`} tone="neg" />
      </div>
      <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
        <StatMini label="Recovery Time" value={`${recovery} lệnh`} />
      </div>
      <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
        <StatMini label="Largest DD Period" value={`${kpis.maxDrawdown.toFixed(1)}%`} tone="neg" />
      </div>
    </div>
  );
}

function DiffCard({ label, a, b, unit }: { label: string; a: number; b: number; unit: string }) {
  const diff = b - a;
  const tone = diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-rose-600' : 'text-slate-500';
  const sign = diff > 0 ? '+' : '';
  return (
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-2 flex items-end justify-between">
        <div className="font-mono text-sm">
          <span className="text-slate-400">{a.toFixed(2)}{unit} →</span>
          <span className="ml-1 font-bold text-slate-800">{b.toFixed(2)}{unit}</span>
        </div>
        <span className={`font-mono text-sm font-bold ${tone}`}>{sign}{diff.toFixed(2)}{unit}</span>
      </div>
    </div>
  );
}

export function PeriodComparison({ trades }: { trades: Trade[] }) {
  if (trades.length < 4) {
    return (
      <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
        Không đủ dữ liệu để so sánh hai giai đoạn.
      </p>
    );
  }
  const { a, b } = splitPeriod(trades);
  const ka = computeKpis(a);
  const kb = computeKpis(b);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-600">
          Period A <span className="font-mono text-xs text-slate-400">({a[0]?.date} – {a[a.length - 1]?.date})</span>
        </div>
        <i className="ri-arrow-right-line text-slate-300" />
        <div className="text-sm font-semibold text-slate-600">
          Period B <span className="font-mono text-xs text-slate-400">({b[0]?.date} – {b[b.length - 1]?.date})</span>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <DiffCard label="Win Rate" a={ka.winRate} b={kb.winRate} unit="%" />
        <DiffCard label="Net Profit" a={ka.netProfit} b={kb.netProfit} unit="" />
        <DiffCard label="Profit Factor" a={ka.profitFactor === Infinity ? 99 : ka.profitFactor} b={kb.profitFactor === Infinity ? 99 : kb.profitFactor} unit="" />
        <DiffCard label="Drawdown" a={ka.maxDrawdown} b={kb.maxDrawdown} unit="%" />
        <DiffCard label="Trade Count" a={ka.total} b={kb.total} unit="" />
        <DiffCard label="Average Trade" a={ka.avgTrade} b={kb.avgTrade} unit="" />
      </div>
    </div>
  );
}

export function DistributionStats({ trades }: { trades: Trade[] }) {
  const kpis = computeKpis(trades);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <StatMini label="Average Win" value={fmtUsd(kpis.avgWin)} tone="pos" />
      <StatMini label="Average Loss" value={fmtUsd(-kpis.avgLoss)} tone="neg" />
      <StatMini label="Largest Win" value={fmtUsd(kpis.largestWin)} tone="pos" />
      <StatMini label="Largest Loss" value={fmtUsd(kpis.largestLoss)} tone="neg" />
    </div>
  );
}
